// ════════════════════════════════════════════════════════════════════════════
// ⌨️ ShortcutsModal — Phase 2.4 Keyboard shortcut discoverability
// ════════════════════════════════════════════════════════════════════════════
// Triggered by pressing '?' (Shift+/) anywhere in the app (except inside inputs).
// Shows a modal with all power-user shortcuts.
// Self-mounting — just render <ShortcutsModal/> once in the tree.
// ════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Keyboard } from 'lucide-react';

interface Shortcut {
  keys: string[];
  label: string;
  group: string;
}

const SHORTCUTS: Shortcut[] = [
  // Cart
  { keys: ['F2'],           label: 'Focus search',                    group: 'Cart' },
  { keys: ['F3'],           label: 'Open barcode scanner',            group: 'Cart' },
  { keys: ['F4'],           label: 'Hold current bill',               group: 'Cart' },
  { keys: ['F9'],           label: 'Clear cart',                      group: 'Cart' },
  { keys: ['Enter'],        label: 'Auto-add on exact match',         group: 'Cart' },
  // Checkout
  { keys: ['F10'],          label: 'Open payment panel',              group: 'Checkout' },
  { keys: ['F12'],          label: 'Complete sale',                   group: 'Checkout' },
  // Views
  { keys: ['Alt', '1'],     label: 'POS',                             group: 'Navigation' },
  { keys: ['Alt', '2'],     label: 'Analytics / Live Pulse',          group: 'Navigation' },
  { keys: ['Alt', '3'],     label: 'Day-End / Z-Report',              group: 'Navigation' },
  { keys: ['Alt', '4'],     label: 'Cashiers',                        group: 'Navigation' },
  { keys: ['Alt', '5'],     label: 'Settings',                        group: 'Navigation' },
  { keys: ['Esc'],          label: 'Back / close',                    group: 'Navigation' },
  // Luna
  { keys: ['Ctrl', 'L'],    label: 'Summon Luna AI',                  group: 'AI' },
  // General
  { keys: ['?'],            label: 'Show this help',                  group: 'General' },
];

export const ShortcutsModal: React.FC = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't trigger while typing in inputs / textareas / contenteditable
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const groups = Array.from(new Set(SHORTCUTS.map((s) => s.group)));

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="sc-bg"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      >
        <motion.div
          key="sc-card"
          initial={{ scale: 0.94, y: 16, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.94, y: 16, opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="w-full max-w-2xl max-h-[85vh] bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-brand-surface border border-brand-surface-border flex items-center justify-center">
                <Keyboard size={15} className="text-brand-primary" />
              </div>
              <div>
                <h3 className="text-[14px] font-black text-slate-800 leading-tight">Keyboard Shortcuts</h3>
                <p className="text-[10px] text-slate-500">Power-user commands for faster checkout</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors"
              aria-label="Close"
            >
              <X size={15} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
            {groups.map((g) => (
              <div key={g}>
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2.5">{g}</h4>
                <div className="space-y-1.5">
                  {SHORTCUTS.filter((s) => s.group === g).map((s, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 py-1">
                      <span className="text-[12px] text-slate-700">{s.label}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        {s.keys.map((k, j) => (
                          <React.Fragment key={j}>
                            <kbd className="px-1.5 py-0.5 rounded-md border border-slate-200 bg-slate-50 text-slate-700 text-[10px] font-mono font-bold shadow-sm">
                              {k}
                            </kbd>
                            {j < s.keys.length - 1 && <span className="text-slate-300 text-[10px]">+</span>}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer tip */}
          <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 text-center text-[10px] text-slate-500">
            Press <kbd className="px-1 py-0.5 rounded bg-white border border-slate-200 font-mono font-bold">?</kbd> anywhere to toggle this panel
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};
